import axios from "axios";
import Cookies from "js-cookie";
import { logout } from "../utils";
import { Modal } from "antd";
import CustomSuccessIcon from "../components/Icons/CustomSuccessIcon";
import CustomDangerIcon from "../components/Icons/CustomDangerIcon";
import { t } from "i18next";

// Constants
const MINUTE_IN_MS = 60000;
const COOKIE_OPTS = window.location.hostname.endsWith('cloudbook.vn')
    ? { domain: '.cloudbook.vn' }
    : {};
const AUTHENTICATION_FAIL = new Set(["UR0015", "UR0014", "UR0013", "UR0012", "UR0001"]);
const AUTHENTICATION_EXPIRED_CODE = new Set(["UR0006", "UR0009", "UR0016"]);
const SUCCESS_STATUS = new Set(["S0000", "S0001", "S0002"]);
const API_BASE_URL = "https://api.cloudbook.vn/api/v1";
const STAGING_API_BASE_URL = "https://staging.api.cloudbook.vn/api/v1";
const REQUEST_LIMIT = 3;
const REQUEST_INTERVAL = 1000; // 1 second

// Config
const isDevMode =
    window.location.href.includes("localhost") || window.location.href.includes("staging");

const api = axios.create({
    baseURL: isDevMode ? STAGING_API_BASE_URL : API_BASE_URL,
});

// Token refresh lock to prevent race conditions
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
    refreshSubscribers.push(callback);
};

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const accessToken = Cookies.get("access_token");
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        if (!SUCCESS_STATUS.has(response.data?.status)) {
            return Promise.reject(response.data);
        }
        return response;
    },
    async (error) => {
        const { config: originalRequest } = error;
        const status = error?.response?.data?.status;

        if (AUTHENTICATION_EXPIRED_CODE.has(status)) {
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    const { data } = await api.post("/auth/token/refresh", {
                        refresh_token: Cookies.get("refresh_token"),
                    });
                    const newAccessToken = data?.data?.access_token;
                    // Cookies.set("access_token", newAccessToken);
                    // Cookies.set("refresh_token", data?.data?.refresh_token);
                    Cookies.set("access_token", newAccessToken, COOKIE_OPTS);
                    Cookies.set("refresh_token", data?.data?.refresh_token, COOKIE_OPTS);

                    isRefreshing = false;
                    onRefreshed(newAccessToken);

                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    isRefreshing = false;
                    refreshSubscribers = [];
                    console.error("Refresh token failed", refreshError);
                    handleAuthenticationFailure();
                    return Promise.reject(refreshError);
                }
            } else {
                // Token is being refreshed, queue this request
                return new Promise((resolve) => {
                    addRefreshSubscriber((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    });
                });
            }
        }
        if (error?.response?.status === 401 && !originalRequest.url.includes("/auth/token/refresh")) {
            logout();
        }
        return Promise.reject(error);
    }
);

// Shared authentication handler
const handleAuthenticationFailure = () => {
    return Modal.warning({
        title: t("auth.session_expired"),
        content: t("auth.please_login_again"),
        icon: <CustomDangerIcon />,
        centered: true,
        zIndex: 1100,
        onOk: logout,
        onCancel: logout,
    });
};

// Cache configuration helper
export const cacheQueryConfig = ({ time = 3 } = {}) => ({
    staleTime: time * MINUTE_IN_MS,
    gcTime: time * MINUTE_IN_MS, // Replaces deprecated cacheTime in React Query v4+
    keepPreviousData: true,
    refetchOnWindowFocus: false,
});

// Response validation helper
export const validResponse = ({ response }) =>
    SUCCESS_STATUS.has(response?.data?.status || response?.status);

const requestQueue = [];
let requestCount = 0;
let processingQueue = false;

const processQueue = async () => {
    if (processingQueue) return;
    processingQueue = true;

    while (requestQueue.length > 0) {
        if (requestCount < REQUEST_LIMIT) {
            const {
                resolve,
                reject,
                method,
                url,
                data,
                onError,
                onSuccess,
                isShowModalSuccess,
                titleSuccess,
                contentSuccess,
            } = requestQueue.shift();

            requestCount++;
            try {
                const response = await api({ method, url, data });
                isShowModalSuccess &&
                    Modal.success({
                        title: titleSuccess || t("common.success"),
                        content: contentSuccess ? contentSuccess : "",
                        icon: <CustomSuccessIcon />,
                        centered: true,
                        zIndex: 1100,
                    });
                onSuccess(response?.data);
                resolve(response?.data);
            } catch (error) {
                console.log(error);

                if (AUTHENTICATION_FAIL.has(error?.response?.data?.status)) {
                    handleAuthenticationFailure();
                    reject(error);
                    return;
                }

                Modal.error({
                    title: t("common.error_occurred"),
                    content: t(error?.status) || error?.message || "Lỗi không xác định.",
                    icon: <CustomDangerIcon />,
                    centered: true,
                    onOk: onError,
                    onCancel: onError,
                    zIndex: 1100,
                });
                reject(error);
            } finally {
                requestCount--;
                await new Promise((res) => setTimeout(res, REQUEST_INTERVAL / REQUEST_LIMIT));
            }
        } else {
            await new Promise((res) => setTimeout(res, REQUEST_INTERVAL / REQUEST_LIMIT));
        }
    }

    processingQueue = false;
};

export const handleFetchApi = ({
    method = "GET",
    url,
    data = null,
    onError = () => {},
    onSuccess = () => {},
    isShowModalSuccess = false,
    contentSuccess,
    titleSuccess,
}) => {
    if (!url) throw new Error("URL parameter is required");
    return new Promise((resolve, reject) => {
        requestQueue.push({
            resolve,
            reject,
            method,
            url,
            data,
            onError,
            onSuccess,
            isShowModalSuccess,
            contentSuccess,
            titleSuccess,
        });
        processQueue();
    });
};
